import * as React from 'react';
import {
  add,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isEqual,
  isSameMonth,
  isToday,
  parse,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  SearchIcon,
} from 'lucide-react';

import { cn } from '@/src/infrastructure/lib/utils';
import { Button } from '@/src/modules/components/ui/button';
import { Separator } from '@/src/modules/components/ui/separator';
import { useMediaQuery } from '@/src/modules/hooks/use-media-query';

interface Event {
  id: number;
  name: string;
  time: string;
  datetime: string;
}

interface CalendarData {
  day: Date;
  events: Event[];
}

interface FullScreenCalendarProps {
  data: CalendarData[];
  /** Optional handler for the "New Event" button in the header. */
  onNewEvent?: () => void;
  /** Optional handler fired when a day cell is selected. */
  onSelectDay?: (day: Date) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const colStartClasses = [
  '',
  'col-start-2',
  'col-start-3',
  'col-start-4',
  'col-start-5',
  'col-start-6',
  'col-start-7',
];

export function FullScreenCalendar({
  data,
  onNewEvent,
  onSelectDay,
}: FullScreenCalendarProps) {
  const today = startOfToday();
  const [selectedDay, setSelectedDay] = React.useState(today);
  const [currentMonth, setCurrentMonth] = React.useState(
    format(today, 'MMM-yyyy'),
  );
  const firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date());
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const dataByDay = React.useMemo(() => {
    const map = new Map<string, CalendarData[]>();
    for (const d of data) {
      const key = format(d.day, 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    return map;
  }, [data]);

  const days = eachDayOfInterval({
    start: startOfWeek(firstDayCurrentMonth),
    end: endOfWeek(endOfMonth(firstDayCurrentMonth)),
  });

  function handleSelectDay(day: Date) {
    setSelectedDay(day);
    onSelectDay?.(day);
  }

  function previousMonth() {
    const firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 });
    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
  }

  function nextMonth() {
    const firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
  }

  function goToToday() {
    setCurrentMonth(format(today, 'MMM-yyyy'));
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-border/70 bg-surface-lowest shadow-sm">
      {/* Calendar Header */}
      <div className="flex flex-col gap-4 border-b border-border/70 bg-gradient-to-r from-primary/[0.06] to-transparent p-5 md:flex-row md:items-center md:justify-between md:gap-6">
        <div className="flex items-center gap-4">
          <div className="hidden size-16 flex-col items-center justify-center overflow-hidden rounded-xl border border-primary/20 bg-primary/10 shadow-sm md:flex">
            <span className="w-full bg-primary py-0.5 text-center text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
              {format(today, 'MMM')}
            </span>
            <span className="flex flex-1 items-center text-2xl font-bold text-primary">
              {format(today, 'd')}
            </span>
          </div>
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-foreground">
              {format(firstDayCurrentMonth, 'MMMM yyyy')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {format(firstDayCurrentMonth, 'MMM d')} –{' '}
              {format(endOfMonth(firstDayCurrentMonth), 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 md:flex-row md:gap-4">
          <Button
            variant="outline"
            size="icon"
            className="hidden rounded-lg lg:flex"
            aria-label="Search events"
          >
            <SearchIcon size={16} strokeWidth={2} aria-hidden="true" />
          </Button>

          <Separator orientation="vertical" className="hidden h-6 lg:block" />

          <div className="inline-flex w-full items-center rounded-lg border border-border bg-surface-lowest p-0.5 shadow-sm md:w-auto">
            <Button
              onClick={previousMonth}
              className="size-8 rounded-md border-0 shadow-none hover:bg-primary/10 hover:text-primary"
              variant="ghost"
              size="icon"
              aria-label="Navigate to previous month"
            >
              <ChevronLeftIcon size={16} strokeWidth={2} aria-hidden="true" />
            </Button>
            <Button
              onClick={goToToday}
              className="h-8 w-full rounded-md border-0 px-4 text-sm font-medium shadow-none hover:bg-primary/10 hover:text-primary md:w-auto"
              variant="ghost"
            >
              Today
            </Button>
            <Button
              onClick={nextMonth}
              className="size-8 rounded-md border-0 shadow-none hover:bg-primary/10 hover:text-primary"
              variant="ghost"
              size="icon"
              aria-label="Navigate to next month"
            >
              <ChevronRightIcon size={16} strokeWidth={2} aria-hidden="true" />
            </Button>
          </div>

          <Separator
            orientation="horizontal"
            className="block w-full md:hidden"
          />

          <Button
            className="w-full gap-2 rounded-lg shadow-sm md:w-auto"
            onClick={onNewEvent}
          >
            <PlusCircleIcon size={16} strokeWidth={2} aria-hidden="true" />
            <span>New Event</span>
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex flex-1 flex-col">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 border-b border-border/70 bg-surface-low text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {WEEKDAYS.map((weekday) => (
            <div key={weekday} className="py-2.5">
              {weekday}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="flex flex-1 text-xs leading-6">
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-5">
            {days.map((day, dayIdx) =>
              !isDesktop ? (
                <button
                  onClick={() => handleSelectDay(day)}
                  key={dayIdx}
                  type="button"
                  className={cn(
                    !isSameMonth(day, firstDayCurrentMonth) &&
                      'text-muted-foreground',
                    'flex h-14 flex-col border-b border-r border-border/60 px-3 py-2 transition-colors hover:bg-primary/5 focus:z-10',
                  )}
                >
                  <time
                    dateTime={format(day, 'yyyy-MM-dd')}
                    className={cn(
                      'ml-auto flex size-6 items-center justify-center rounded-full',
                      isEqual(day, selectedDay) &&
                        'bg-primary font-semibold text-primary-foreground',
                      !isEqual(day, selectedDay) &&
                        isToday(day) &&
                        'font-semibold text-primary',
                    )}
                  >
                    {format(day, 'd')}
                  </time>
                  {(dataByDay.get(format(day, 'yyyy-MM-dd')) || []).length > 0 && (
                    <div>
                      {(dataByDay.get(format(day, 'yyyy-MM-dd')) || []).map((date) => (
                        <div
                          key={date.day.toString()}
                          className="-mx-0.5 mt-auto flex flex-wrap-reverse"
                        >
                          {date.events.map((event) => (
                            <span
                              key={event.id}
                              className="mx-0.5 mt-1 size-1.5 rounded-full bg-primary"
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              ) : (
                <div
                  key={dayIdx}
                  onClick={() => handleSelectDay(day)}
                  className={cn(
                    dayIdx === 0 && colStartClasses[getDay(day)],
                    !isSameMonth(day, firstDayCurrentMonth) &&
                      'bg-surface-low/60 text-muted-foreground',
                    isToday(day) && 'bg-primary/[0.04]',
                    isEqual(day, selectedDay) &&
                      'bg-primary/[0.07] ring-1 ring-inset ring-primary/40',
                    'group relative flex min-h-[6.5rem] cursor-pointer flex-col border-b border-r border-border/60 transition-colors hover:bg-primary/5 focus:z-10',
                  )}
                >
                  <header className="flex items-center justify-between p-2">
                    <span
                      className={cn(
                        'flex size-7 items-center justify-center rounded-full text-xs transition-colors',
                        isToday(day) &&
                          !isEqual(day, selectedDay) &&
                          'font-bold text-primary',
                        isEqual(day, selectedDay) &&
                          'bg-primary font-semibold text-primary-foreground shadow-sm',
                        !isToday(day) &&
                          !isEqual(day, selectedDay) &&
                          'font-medium group-hover:bg-primary/10',
                      )}
                    >
                      <time dateTime={format(day, 'yyyy-MM-dd')}>
                        {format(day, 'd')}
                      </time>
                    </span>
                  </header>
                  <div className="flex-1 space-y-1 px-2 pb-2">
                    {(dataByDay.get(format(day, 'yyyy-MM-dd')) || [])
                      .map((dayData) => (
                        <React.Fragment key={dayData.day.toString()}>
                          {dayData.events.slice(0, 2).map((event) => (
                            <div
                              key={event.id}
                              className="flex flex-col gap-0.5 rounded-md border-l-2 border-primary bg-primary/10 px-2 py-1 text-xs leading-tight"
                            >
                              <p className="truncate font-medium text-primary">
                                {event.name}
                              </p>
                              <p className="leading-none text-primary/70">
                                {event.time}
                              </p>
                            </div>
                          ))}
                          {dayData.events.length > 2 && (
                            <div className="px-1 text-[11px] font-medium text-muted-foreground">
                              + {dayData.events.length - 2} more
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              ),
            )}
          </div>

          <div className="isolate grid w-full auto-rows-fr grid-cols-7 lg:hidden">
            {days.map((day, dayIdx) => (
              <button
                onClick={() => handleSelectDay(day)}
                key={dayIdx}
                type="button"
                className={cn(
                  !isSameMonth(day, firstDayCurrentMonth) &&
                    'text-muted-foreground',
                  isToday(day) && 'bg-primary/[0.04]',
                  'flex h-16 flex-col border-b border-r border-border/60 px-2 py-2 transition-colors hover:bg-primary/5 focus:z-10',
                )}
              >
                <time
                  dateTime={format(day, 'yyyy-MM-dd')}
                  className={cn(
                    'ml-auto flex size-7 items-center justify-center rounded-full text-xs',
                    isEqual(day, selectedDay) &&
                      'bg-primary font-semibold text-primary-foreground',
                    !isEqual(day, selectedDay) &&
                      isToday(day) &&
                      'font-bold text-primary',
                  )}
                >
                  {format(day, 'd')}
                </time>
                {(dataByDay.get(format(day, 'yyyy-MM-dd')) || []).length > 0 && (
                  <div>
                    {(dataByDay.get(format(day, 'yyyy-MM-dd')) || []).map((date) => (
                      <div
                        key={date.day.toString()}
                        className="-mx-0.5 mt-auto flex flex-wrap-reverse"
                      >
                        {date.events.map((event) => (
                          <span
                            key={event.id}
                            className="mx-0.5 mt-1 size-1.5 rounded-full bg-primary"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export type { CalendarData, Event as CalendarEvent };
